import {Title} from '@radix-ui/react-dialog'
import React from 'react'
import styled from 'styled-components'

import {HighlightItemContainer} from '@/components/highlight-items/highlight-items'
import Image from '@/components/image'
import {SimpleSignupForm} from '@/components/simple-signup-form'
import {Stack} from '@/components/stack'
import {Text} from '@/components/text'
import type {UnifiedSignupModalQuery} from '@/graphql-types'
import useSignup, {type Args} from '@/hooks/use-signup'
import {useGlobalStrings} from '@/providers/global-strings'
import getEnvironmentField from '@/utils/get-environment-field'
import mq from '@/utils/media-query'

type LeftColumnProps = {
    signupProps: Args
    content: UnifiedSignupModalQuery['allPrismicUnifiedSignupModal']['nodes'][number]
}

export const LeftColumn = ({signupProps, content}: LeftColumnProps) => {
    const globalStrings = useGlobalStrings()

    const {
        title,
        subtitle,
        placeholder,
        submitText,
        ariaLabel,
        errorText,
        highlightItems,
        ...form
    } = content.data

    const formConfig = getEnvironmentField({
        production: form.form_production,
        staging: form.form_staging,
    })
    const signupFormProps = useSignup(signupProps, formConfig.formId)

    return (
        <StyledContainer>
            <Stack $space={24} $stretch>
                <Stack $space={4} $stretch>
                    <Title asChild>
                        <StyledTitle as="h2" $variant="title-base">
                            {title}
                        </StyledTitle>
                    </Title>
                    <StyledSubtitle $variant="paragraph-base" $color="lightMuted" $center>
                        {subtitle}
                    </StyledSubtitle>
                </Stack>
                <StyledStack $space={24} $stretch>
                    <SimpleSignupForm
                        {...signupFormProps}
                        errorText={errorText}
                        placeholder={placeholder}
                        submitText={submitText || globalStrings.submit}
                        ariaLabel={ariaLabel}
                        withBorder
                        withErrorAnimation
                    />
                    <HighlightItemContainer>
                        {highlightItems.map(({text, image}) => (
                            <HighlightItem key={text}>
                                <Image image={image} />
                                <Text $variant="label-base-medium">{text}</Text>
                            </HighlightItem>
                        ))}
                    </HighlightItemContainer>
                </StyledStack>
            </Stack>
        </StyledContainer>
    )
}

const StyledContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    padding-block: ${({theme}) => theme.spacings.s48};
    padding-inline: ${({theme}) => theme.spacings.s20};
    ${mq.min('md')} {
        padding-inline: ${({theme}) => theme.spacings.s48};
    }
`

const StyledStack = styled(Stack)`
    margin-top: ${({theme}) => theme.spacings.s8};
`

const StyledTitle = styled(Text)`
    text-align: center;
`

const StyledSubtitle = styled(Text)`
    text-wrap: balance;
    text-align: center;
`

const HighlightItem = styled.li`
    color: ${({theme}) => theme.colors.text.default};
    display: flex;
    align-items: center;
    gap: ${({theme}) => theme.spacings.s8};
`
